import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getTaskStatus } from '../APIManager/BulkAddCourseSectionManger'
import { notification } from '../components/Notifications'
import { useUserRole } from './useUserRole'

const TERMINAL_STATUSES = new Set(['SUCCESSFUL', 'WARNING', 'ERROR', 'COMPLETED', 'FAILED', 'REVOKED'])
const BASE_INTERVAL_MS = 4000
const MAX_INTERVAL_MS = 60000

export const useTaskStatus = (storageKey?: string): {
  taskId: string | null
  setTaskId: (id: string | null) => void
  showBackgroundModal: boolean
  setShowBackgroundModal: (open: boolean) => void
  isCheckingStatus: boolean
  checkStatusBeforeExport: () => Promise<boolean>
} => {
  const { isGlobalRole } = useUserRole()
  const [taskId, setTaskIdState] = useState<string | null>(() => {
    if (storageKey) return sessionStorage.getItem(storageKey) ?? null
    return null
  })
  const [showBackgroundModal, setShowBackgroundModal] = useState(false)
  const pollCountRef = useRef(0)
  const handledRef = useRef(false)

  const setTaskId = (id: string | null): void => {
    if (id) {
      handledRef.current = false
      pollCountRef.current = 0
    }
    setTaskIdState(id)
    if (storageKey) {
      if (id) sessionStorage.setItem(storageKey, id)
      else sessionStorage.removeItem(storageKey)
    }
  }

  const { data: taskData } = useQuery({
    queryKey: ['exportBackgroundTask', storageKey ?? '', taskId],
    queryFn: async () => {
      if (!taskId) throw new Error('No taskId')
      return await getTaskStatus(taskId)
    },
    enabled: !!taskId && isGlobalRole,
    refetchInterval: (query) => {
      const status = query.state.data?.data?.status as string | undefined
      if (status != null && TERMINAL_STATUSES.has(status)) return false
      return Math.min(BASE_INTERVAL_MS * Math.pow(2, pollCountRef.current), MAX_INTERVAL_MS)
    }
  })

  useEffect(() => {
    if (!taskData) return
    const status = taskData?.data?.status as string | undefined
    if (!status) return
    if (!TERMINAL_STATUSES.has(status)) {
      pollCountRef.current += 1
      return
    }
    if (handledRef.current) return
    handledRef.current = true
    const result = taskData?.data?.result as string | undefined
    const isSuccess = status === 'SUCCESSFUL' || status === 'WARNING' ||
      (status === 'COMPLETED' && result !== 'ERROR')
    if (isSuccess) {
      notification('Export complete', { variant: 'success' })
    } else {
      notification('Export failed. Please try again.', { variant: 'error' })
    }
    setTaskId(null)
    setShowBackgroundModal(false)
  }, [taskData])

  // Polling already tracks running state; non-global roles cannot poll task status.
  const checkStatusBeforeExport = async (): Promise<boolean> => {
    if (!taskId || !isGlobalRole) return true
    setShowBackgroundModal(true)
    return false
  }

  return {
    taskId,
    setTaskId,
    showBackgroundModal,
    setShowBackgroundModal,
    isCheckingStatus: !!taskId,
    checkStatusBeforeExport
  }
}
